import { useCallback, useEffect, useState } from "react";
import { fetchCustomerById, fetchCustomerOrders } from "../services/customersService";

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

const useCustomerDetail = (id) => {
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadCustomer = useCallback(async () => {
    if (!id) {
      setCustomer(null);
      setOrders([]);
      setTransactions([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetchCustomerById(id);
      if (response.success) {
        const data = response.data || {};
        setCustomer(data);
        setTransactions(
          data.wallet_transactions || data.transactions || data.walletHistory || []
        );
      } else {
        setError(response.message || DEFAULT_ERROR_MESSAGE);
      }
    } catch (err) {
      console.error("Error fetching customer detail:", err);
      setError("Failed to fetch customer detail. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadOrders = useCallback(async () => {
    if (!id) {
      setOrders([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetchCustomerOrders(id);
      if (response.success) {
        const payload = response.data;
        // Accept common shapes: { items: [...] } or direct array, or { orders: [...] }
        const items = Array.isArray(payload)
          ? payload
          : payload?.items || payload?.orders || [];
        setOrders(items || []);
      } else {
        setError(response.message || DEFAULT_ERROR_MESSAGE);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to fetch orders. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [id]);


  const resetAlerts = useCallback(() => {
    setError(null);
  }, []);

  const refresh = useCallback(() => {
    loadCustomer();
    loadOrders();
  }, [loadCustomer, loadOrders]);

  useEffect(() => {
    loadCustomer();
    loadOrders();
  }, [loadCustomer, loadOrders]);

  return {
    customer,
    orders,
    transactions,
    loading,
    error,
    resetAlerts,
    refresh,
  };
};

export default useCustomerDetail;
